import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TopHeader from './TopHeader';
import Sidebar from './Sidebar';
import { useAlerts, startComplianceScheduler } from '../../jobs/schedulers';

const AppLayout = ({ children, notifications = [] }) => {
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Alert system integration
  const { alerts, alertCount } = useAlerts();

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setIsSidebarCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize compliance scheduler when app layout mounts
  useEffect(() => {
    // Start the compliance scheduler for automated monitoring
    startComplianceScheduler();
    
    return () => {
      // Cleanup if needed when component unmounts
    };
  }, []);

  // Pages that should not show the sidebar (full-screen pages)
  const noSidebarPages = [
    '/',
    '/login',
    '/tenant-selection',
    '/marketing-landing-page'
  ];

  const shouldShowSidebar = !noSidebarPages?.includes(window?.location?.pathname);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  // Mock current tenant and user data
  const currentTenant = {
    name: 'Acme Holdings',
    logo: '/assets/images/tenant-logo.png'
  };

  const currentUser = {
    name: 'John Smith',
    role: 'M&A Lead',
    avatar: '/assets/images/user-avatar.png'
  };

  // Combine notifications with alerts for header display
  const combinedNotifications = [
    ...notifications,
    // Convert alerts to notification format for header
    ...(alerts?.slice(0, 5)?.map(alert => ({
      id: alert?.id,
      title: alert?.title,
      message: alert?.message,
      time: 'Now',
      read: false,
      type: 'alert',
      severity: alert?.severity,
      alert: alert // Include full alert data
    })) || [])
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Header - Always visible with alert-enhanced notifications */}
      <TopHeader
        currentTenant={currentTenant}
        user={currentUser}
        notifications={combinedNotifications}
        onToggleSidebar={toggleSidebar}
        alertCount={alertCount} // Pass alert count to header
      />
      <div className="flex">
        {/* Sidebar - Only on app pages, with alert badges */}
        {shouldShowSidebar && (
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            onToggle={toggleSidebar}
          />
        )}

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ${
          shouldShowSidebar 
            ? `${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16`
            : 'pt-16'
        }`}>
          {/* Alert notification banner for critical alerts */}
          {alerts?.filter(alert => alert?.severity === 'critical')?.length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 mx-4 mt-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    <strong>Critical Alert:</strong> {alerts?.filter(alert => alert?.severity === 'critical')?.length} critical issue{alerts?.filter(alert => alert?.severity === 'critical')?.length > 1 ? 's' : ''} require immediate attention.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Add spacing container wrapper */}
          <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1920px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;